import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import {
  checkTemplate,
  explainCost,
  MAX_TEMPLATE_BODY,
  measure,
  smsFooter,
  withSmsFooter,
} from "@how/communications";
import { ConfirmActionModal, PageHeader } from "@how/ui";
import { useTenantRecord } from "../tenant/queries";
import type { AudienceChannel } from "./audience-queries";
import { CommunicationsTabs } from "./CommunicationsTabs";
import {
  type TemplateRow,
  useDeleteTemplate,
  useSaveTemplate,
  useTemplates,
} from "./communications-queries";
import { useSmsSender } from "./sms-sender-queries";

/** The parameters the platform can fill today. A template asking for another is broken at write time. */
const KNOWN_PARAMETERS = ["first_name", "church_name"] as const;

const CHANNEL_LABEL: Record<AudienceChannel, string> = { sms: "Text", email: "Email" };

/**
 * The name every text ends with, as the database writes it: the registered sender's when the church's texts go out
 * under a registration (DCX Logic's pilot sender while pilot texting is on, ADR 0072), otherwise the church's.
 */
function useFooterName(): string | undefined {
  const church = useTenantRecord().data?.name;
  const sender = useSmsSender().data;
  return sender !== undefined && sender.senderName !== "" ? sender.senderName : church;
}

/**
 * Templates (communications plan task 7): what a campaign says. The editor measures a text message the way a
 * carrier will, live, so a church sees "2 segments" before it sees the bill. Every count includes the opt-out line
 * HoW adds to each text, because the carrier counts it too.
 */
export function TemplatesPage() {
  const canCompose = usePermission("communications.compose", { write: true });
  const church = useFooterName();
  const templates = useTemplates();
  const remove = useDeleteTemplate();
  const [editing, setEditing] = useState<TemplateRow | null | "new">(null);
  const [deleting, setDeleting] = useState<TemplateRow | null>(null);

  return (
    <>
      <PageHeader
        title="Communications"
        description="Who the church can reach, what it says, and what it has sent."
        actions={
          canCompose.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditing("new");
              }}
            >
              New template
            </Button>
          ) : null
        }
      />
      <CommunicationsTabs active="templates" />
      <Stack gap="md">
        {(templates.data ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            No templates yet.
          </Text>
        ) : (
          <div className="how-table-frame" style={{ overflowX: "auto" }}>
            <Table
              verticalSpacing={9}
              className="how-table"
              highlightOnHover
              aria-label="Templates"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Channel</Table.Th>
                  <Table.Th>Text</Table.Th>
                  <Table.Th className="how-numeric">Segments</Table.Th>
                  <Table.Th aria-label="Actions" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(templates.data ?? []).map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{ cursor: "pointer" }}
                    tabIndex={0}
                    onClick={() => {
                      setEditing(row);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") setEditing(row);
                    }}
                  >
                    <Table.Td>
                      <Group gap={6}>
                        <Text size="sm" fw={500}>
                          {row.name}
                        </Text>
                        {row.isActive ? null : (
                          <Badge variant="light" color="gray">
                            Inactive
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{CHANNEL_LABEL[row.channel]}</Table.Td>
                    <Table.Td>
                      {/* Table cells never wrap, so the preview bounds its own width; the row opens the whole text. */}
                      <Text
                        size="sm"
                        truncate="end"
                        maw={420}
                        title={row.subject === null ? row.body : `${row.subject} — ${row.body}`}
                      >
                        {row.subject === null ? row.body : `${row.subject} — ${row.body}`}
                      </Text>
                    </Table.Td>
                    <Table.Td className="how-numeric">
                      {row.channel === "sms"
                        ? measure(withSmsFooter(row.body, church)).segments
                        : ""}
                    </Table.Td>
                    <Table.Td>
                      {canCompose.data === true ? (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleting(row);
                          }}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Stack>

      {editing === null ? null : (
        <TemplateModal
          key={editing === "new" ? "new" : editing.id}
          template={editing === "new" ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      <ConfirmActionModal
        opened={deleting !== null}
        title="Delete template"
        message={`"${deleting?.name ?? ""}" will no longer be offered to campaigns. Campaigns already approved keep the text they were approved with.`}
        confirmLabel="Delete"
        danger
        loading={remove.isPending}
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate(deleting.id, {
              onSuccess: () => {
                setDeleting(null);
              },
            });
          }
        }}
      />
    </>
  );
}

function TemplateModal({
  template,
  onClose,
}: {
  readonly template: TemplateRow | null;
  readonly onClose: () => void;
}) {
  const [name, setName] = useState(template?.name ?? "");
  const [channel, setChannel] = useState<AudienceChannel>(template?.channel ?? "sms");
  const [body, setBody] = useState(template?.body ?? "");
  const [subject, setSubject] = useState(template?.subject ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const save = useSaveTemplate();
  const church = useFooterName();

  const problems = checkTemplate(body, [...KNOWN_PARAMETERS]);
  // What the carrier counts: the message and the opt-out line the database adds to every text.
  const sent = withSmsFooter(body, church);
  const measurement = measure(sent);
  const cost = explainCost(sent);

  return (
    <Modal
      opened
      onClose={onClose}
      title={template === null ? "New template" : "Edit template"}
      size="lg"
    >
      <Stack gap="md">
        <Group grow align="flex-start">
          <TextInput
            label="Name"
            required
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Channel
            </Text>
            <SegmentedControl
              value={channel}
              onChange={(value) => {
                setChannel(value === "email" ? "email" : "sms");
              }}
              data={[
                { value: "sms", label: "Text" },
                { value: "email", label: "Email" },
              ]}
            />
          </Stack>
        </Group>
        {channel === "email" ? (
          <TextInput
            label="Subject"
            required
            maxLength={200}
            description="The subject line. {{first_name}} and {{church_name}} work here too."
            value={subject}
            onChange={(event) => {
              setSubject(event.currentTarget.value);
            }}
          />
        ) : null}
        <Textarea
          label="Text"
          required
          autosize
          minRows={3}
          maxLength={MAX_TEMPLATE_BODY}
          description={`Use {{first_name}} or {{church_name}} to fill in the person's name or the church's. ${String(body.length)} of ${String(MAX_TEMPLATE_BODY)} characters.`}
          value={body}
          onChange={(event) => {
            setBody(event.currentTarget.value);
          }}
        />
        {channel === "sms" && body.length > 0 ? (
          <Text size="sm" c={measurement.segments > 1 ? "orange" : "dimmed"}>
            {measurement.segments === 1
              ? "1 text message"
              : `${String(measurement.segments)} text messages`}{" "}
            per person ({measurement.encoding === "gsm-7" ? "standard" : "unicode"} encoding,{" "}
            {String(measurement.remaining)} characters left in this one).
            {cost === null ? "" : ` ${cost}`}
          </Text>
        ) : null}
        {channel === "sms" ? (
          <Text size="xs" c="dimmed">
            Every text ends with "{smsFooter(church).trim()}", so people know who is texting and how
            to stop. HoW adds it and it is counted above.
          </Text>
        ) : null}
        {problems.length === 0 ? null : (
          <Alert color="orange" variant="light">
            {problems.map((p) => p.message).join(" ")}
          </Alert>
        )}
        <TextInput
          label="Description"
          value={description}
          onChange={(event) => {
            setDescription(event.currentTarget.value);
          }}
        />
        <Switch
          label="Offered to new campaigns"
          checked={isActive}
          onChange={(event) => {
            setIsActive(event.currentTarget.checked);
          }}
        />
        {save.isError ? (
          <Alert color="red" variant="light">
            {save.error.message}
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={
              name.trim() === "" ||
              problems.length > 0 ||
              (channel === "email" && subject.trim() === "")
            }
            onClick={() => {
              save.mutate(
                {
                  templateId: template?.id ?? null,
                  name: name.trim(),
                  channel,
                  body,
                  subject: subject.trim(),
                  description,
                  isActive,
                },
                { onSuccess: onClose },
              );
            }}
          >
            Save template
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
